import {
  formCollectionTypesEnum,
  getFormCollectionTranslationKey,
  getFormCollectionTypeByFormType,
} from 'scenes/ProjectForms/utils/formCollections';

export const PROJECT_BANK_FORMS_ROOT_ID_PREFIX = 'project-bank-forms-root__';
export const PROJECT_BANK_FORMS_FOLDER_ID_PREFIX =
  'project-bank-forms-folder__';
export const PROJECT_BANK_FORMS_FILE_ID_PREFIX = 'project-bank-forms-file__';

const ORIENTATION_FORMS_COLLECTION_FOLDER_SUFFIX = `__collection__${formCollectionTypesEnum.orientation_forms}`;

export const isOrientationFormsCollectionFolderId = (folderId) =>
  typeof folderId === 'string' &&
  folderId.startsWith(PROJECT_BANK_FORMS_FOLDER_ID_PREFIX) &&
  folderId.endsWith(ORIENTATION_FORMS_COLLECTION_FOLDER_SUFFIX);

export const createProjectBankFormsRootFolderId = (projectId) =>
  `${PROJECT_BANK_FORMS_ROOT_ID_PREFIX}${projectId}`;

export const createProjectBankFormsFolderId = (
  projectId,
  folderKind,
  folderKey
) =>
  `${PROJECT_BANK_FORMS_FOLDER_ID_PREFIX}${projectId}__${folderKind}__${folderKey}`;

export const createProjectBankFormsFileId = (formId) =>
  `${PROJECT_BANK_FORMS_FILE_ID_PREFIX}${formId}`;

export const isProjectBankFormsFolderId = (folderId = '') =>
  folderId.startsWith(PROJECT_BANK_FORMS_ROOT_ID_PREFIX) ||
  folderId.startsWith(PROJECT_BANK_FORMS_FOLDER_ID_PREFIX);

export const getProjectIdFromProjectBankFormsFolderId = (folderId = '') => {
  const [projectId] = folderId.replace(PROJECT_BANK_FORMS_ROOT_ID_PREFIX, '')
    .replace(PROJECT_BANK_FORMS_FOLDER_ID_PREFIX, '')
    .split('__');

  return projectId || null;
};

export const isProjectTypeProjectBankFolder = (folder) => {
  if (!folder || folder.type !== 'FOLDER' || !folder.key) {
    return false;
  }

  const keyDepth = folder.key.split('/').length;

  return (
    (folder.businessId && keyDepth === 3) || (!folder.businessId && keyDepth === 2)
  );
};

const ensurePdfFileName = (fileName) => {
  if (!fileName) {
    return 'form.pdf';
  }

  return /\.pdf$/i.test(fileName) ? fileName : `${fileName}.pdf`;
};

const getProjectBankFormS3Key = (project, formId) => {
  if (project?.business_id) {
    return `project-bank/${project.business_id}/${project.projectKey}/forms/${formId}.pdf`;
  }

  return `project-bank/${project.projectKey}/forms/${formId}.pdf`;
};

export const createProjectBankFormsRootFolder = (projectFolder) => {
  if (!projectFolder) {
    return null;
  }

  return {
    id: createProjectBankFormsRootFolderId(projectFolder.project?.id || projectFolder.project),
    createdAt: projectFolder.createdAt,
    updatedAt: projectFolder.updatedAt,
    name: 'forms',
    type: 'FOLDER',
    key: `${projectFolder.key}/forms`,
    parentKey: projectFolder.key,
    path: `${projectFolder.path}/forms`,
    parentPath: projectFolder.path,
    parent: projectFolder.id,
    businessId: projectFolder.businessId,
    project: projectFolder.project,
    user: { id: '', firstName: '', lastName: '' },
    context: 'project-bank-forms',
    isVirtualProjectBankForm: true,
    isReadOnly: true,
  };
};

export const buildProjectBankFormItems = ({
  forms,
  project,
  projectFolder,
  templatesByGroupId,
  usersById,
}) => {
  if (!project || !projectFolder) {
    return { folders: [], files: [] };
  }

  const formsRootFolder = createProjectBankFormsRootFolder(projectFolder);

  if (!formsRootFolder) {
    return { folders: [], files: [] };
  }

  const foldersById = {};
  const files = [];

  forms.forEach((form) => {
    const templateGroupId = form.template_group_id || null;
    const normalizedFormType = form.originalType || form.type || null;
    // Place measurement_report_attachment forms next to their parent
    // substation_commissioning_inspection_report (they share a folder so
    // users selecting from that folder grab both — matching the bundled
    // download behaviour in ProjectForms' useDownloadPdf).
    const folderFormType =
      normalizedFormType === 'measurement_report_attachment'
        ? 'substation_commissioning_inspection_report'
        : normalizedFormType;
    const collectionType = templateGroupId
      ? null
      : getFormCollectionTypeByFormType(folderFormType);

    const folderKind = templateGroupId ? 'template' : 'collection';
    const folderKey = templateGroupId || collectionType || folderFormType || 'other';
    const folderId = createProjectBankFormsFolderId(
      project.id,
      folderKind,
      folderKey
    );
    const folderPath = `${formsRootFolder.path}/${folderKind}/${folderKey}`;
    const template = templateGroupId ? templatesByGroupId[templateGroupId] : null;
    const translationKey = collectionType
      ? getFormCollectionTranslationKey(collectionType)
      : null;
    const user =
      (form.mirrorUser && usersById[form.mirrorUser]) || {
        id: form.mirrorUser || '',
        firstName: '',
        lastName: '',
      };

    if (!foldersById[folderId]) {
      foldersById[folderId] = {
        id: folderId,
        createdAt: form.createdAt,
        updatedAt: form.createdAt,
        name:
          template?.name || collectionType || normalizedFormType || 'forms',
        translationKey,
        type: 'FOLDER',
        key: `${formsRootFolder.key}/${folderKind}/${folderKey}`,
        parentKey: formsRootFolder.key,
        path: folderPath,
        parentPath: formsRootFolder.path,
        parent: formsRootFolder.id,
        businessId: project.business_id,
        project: project.id,
        user: { id: '', firstName: '', lastName: '' },
        context: 'project-bank-forms',
        isVirtualProjectBankForm: true,
        isReadOnly: true,
      };
    }

    if (new Date(form.createdAt || 0) > new Date(foldersById[folderId].createdAt || 0)) {
      foldersById[folderId].createdAt = form.createdAt;
      foldersById[folderId].updatedAt = form.createdAt;
    }

    files.push({
      id: createProjectBankFormsFileId(form.id),
      sourceFormId: form.id,
      createdAt: form.createdAt,
      updatedAt: form.createdAt,
      name: ensurePdfFileName(form.formName || form.id),
      type: 'FILE',
      key: getProjectBankFormS3Key(project, form.id),
      parentKey: foldersById[folderId].key,
      path: `${folderPath}/${form.id}`,
      parentPath: folderPath,
      parent: folderId,
      businessId: project.business_id,
      project: project.id,
      user,
      mirrorUser: form.mirrorUser || '',
      context: 'project-bank-forms',
      isVirtualProjectBankForm: true,
      isReadOnly: false,
      file: {
        key: getProjectBankFormS3Key(project, form.id),
        mimeType: 'application/pdf',
      },
    });
  });

  return {
    folders: Object.values(foldersById),
    files,
  };
};
